"use client";

import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Conteúdo do cabeçalho fixo (título, ícone). */
  title?: React.ReactNode;
  /** Conteúdo do rodapé fixo (botões de ação). */
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export function Dialog({ open, onClose, title, footer, children, className }: DialogProps) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={cn(
          "relative z-10 flex w-full max-w-md flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl",
          "max-h-[85svh]",
          className,
        )}
      >
        {/* Header fixo */}
        {title && (
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-5 py-4">
            <span className="text-[15px] font-bold leading-snug text-foreground">{title}</span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border bg-secondary text-muted-foreground transition-colors hover:bg-muted"
            >
              <X className="size-3" />
            </button>
          </div>
        )}

        {/* Corpo rolável */}
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {children}
        </div>

        {/* Footer fixo */}
        {footer && (
          <div className="flex shrink-0 items-center gap-2 border-t border-border px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

// Sub-componentes legados mantidos para compatibilidade
interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}
export function DialogContent({ children, className = "" }: DialogContentProps) {
  return (
    <div className={cn("bg-card border border-border rounded-2xl shadow-2xl", className)}>
      {children}
    </div>
  );
}

export function DialogHeader({ children }: { children: React.ReactNode }) {
  return <div className="mb-4">{children}</div>;
}

export function DialogTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="text-[15px] font-bold text-foreground">{children}</h2>;
}

export function DialogFooter({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 flex justify-end gap-3">{children}</div>;
}
